import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UserService } from 'app/core/user/user.service';
import {
    first,
    from,
    mergeMap,
    Observable,
    of,
    switchMap,
    throwError,
} from 'rxjs';

import { AngularFireAuth } from '@angular/fire/compat/auth';
import firebase from 'firebase/compat/app';
import { TrainerService } from '../user/trainer.service';
import { GymService } from '../user/gym.service';

export interface Item {
    name: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
    private loggedInUser: firebase.User;

    constructor(
        private _httpClient: HttpClient,
        private _userService: UserService,
        public authService: AngularFireAuth,
        private _trainerService: TrainerService,
        private _gymService: GymService
    ) {
        this.authService.authState.subscribe((nextUser: firebase.User) => {
            console.log('authStateChanged', nextUser);
            this.loggedInUser = nextUser;
            this._trainerService.user = nextUser;
            this._gymService.user = nextUser;
            this._userService.user = nextUser;
        });
    }

    forgotPassword(email: string): Observable<any> {
        return this._httpClient.post('api/auth/forgot-password', email);
    }

    resetPassword(password: string): Observable<any> {
        return this._httpClient.post('api/auth/reset-password', password);
    }

    signIn(credentials: { email: string; password: string }): Observable<any> {
        if (this.loggedInUser) {
            return throwError(() => new Error('User is already logged in.'));
        }
        return from(
            this.authService.signInWithEmailAndPassword(
                credentials.email,
                credentials.password
            )
        );
    }

    signOut(): Observable<any> {
        return from(this.authService.signOut());
    }

    signUp(user: {
        name: string;
        email: string;
        password: string;
        company: string;
    }): Observable<void> {
        return from(
            this.authService.createUserWithEmailAndPassword(
                user.email,
                user.password
            )
        ).pipe(
            mergeMap((credential) =>
                this._trainerService.createTrainer(
                    credential.user.uid,
                    user.name
                )
            )
        );
    }

    unlockSession(credentials: {
        email: string;
        password: string;
    }): Observable<any> {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    jwt(): Observable<string> {
        return this.authService.idTokenResult.pipe(
            first(),
            switchMap((idToken) => {
                if (idToken?.token) {
                    return of(idToken.token);
                }
                return of('');
            })
        );
    }

    check(): Observable<boolean> {
        return this.authService.user.pipe(
            first(),
            switchMap((user) => {
                if (user) {
                    return of(true);
                }
                return of(false);
            })
        );
    }
}
